package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.ActualWorker
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftSplits
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.WorkHoursDaySummary
import com.evoapps.evoshift.domain.model.WorkHoursDecision
import com.evoapps.evoshift.domain.model.WorkHoursRow
import com.evoapps.evoshift.domain.model.WorkHoursRules
import com.evoapps.evoshift.domain.model.asksWhoWorked
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.shift.ConfirmWorkHoursUseCase
import com.evoapps.evoshift.domain.usecase.shift.RecordActualWorkUseCase
import com.evoapps.evoshift.domain.usecase.shift.RecordShiftWorkerUseCase
import com.evoapps.evoshift.domain.usecase.shift.SplitShiftUseCase
import com.evoapps.evoshift.domain.usecase.shift.UndoShiftSplitUseCase
import com.evoapps.evoshift.domain.usecase.timeZoneOrNull
import com.evoapps.evoshift.domain.usecase.user.PersonNames
import com.evoapps.evoshift.domain.usecase.user.ResolvePersonNamesUseCase
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.roster.SplitRequest
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.actual_work_saved_nobody
import evoshift.composeapp.generated.resources.actual_work_saved_planned
import evoshift.composeapp.generated.resources.error_no_pay_rates
import evoshift.composeapp.generated.resources.split_undone
import evoshift.composeapp.generated.resources.who_worked_missing_rates
import evoshift.composeapp.generated.resources.who_worked_nobody_done
import evoshift.composeapp.generated.resources.who_worked_recorded
import com.evoapps.evoshift.ui.util.personNameArg
import evoshift.composeapp.generated.resources.work_hours_done_day
import evoshift.composeapp.generated.resources.work_hours_done_one
import evoshift.composeapp.generated.resources.work_hours_done_partly
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

data class WorkHoursUiState(
    val isLoading: Boolean = true,
    /** The month could not be read at all: the screen offers Try again. */
    val loadError: UiText? = null,
    val capabilities: Set<Capability> = emptySet(),
    /** The workplace's today and its local time now ("YYYY-MM-DDTHH:mm:ss"). */
    val today: String? = null,
    val nowLocal: String = "",
    val selectedDate: String? = null,
    /** The month's shifts as shown (a confirmation shows at once, before the server answers). */
    val shifts: List<Shift> = emptyList(),
    /** The month on screen ("YYYY-MM"), whose shifts [shifts] are. */
    val month: String? = null,
    /** SH-235: everyone's names by user id (the shared resolver). */
    val names: Map<String, String> = emptyMap(),
    /** The team, for "Who worked" and "Split shift". */
    val staff: List<User> = emptyList(),
    /** Days closed in the daily results (readable by those who see the money): final. */
    val lockedDays: Set<String> = emptySet(),
    /** Shifts whose confirmation is on its way. */
    val busy: Set<String> = emptySet(),
    val message: UiText? = null,
    val error: UiText? = null,
    /** The SH-236 actions are offered (their use cases are bound). */
    val actualWorkOffered: Boolean = false
) {
    /** Confirming, editing and "who worked" (not once the plan ended, SH-65). */
    val mayConfirm: Boolean get() = capabilities.shows(Gate.WORK_HOURS_EDITING)

    fun nameOf(uid: String): String? = (names[uid] ?: staff.firstOrNull { it.uid == uid }?.displayName)?.takeIf { it.isNotBlank() }

    fun rows(date: String): List<WorkHoursRow> {
        val day = today ?: return emptyList()
        return WorkHoursRules.rows(shifts, date, day, nowLocal) { nameOf(it) ?: PersonNames.UNNAMED }
    }

    fun summary(date: String): WorkHoursDaySummary? {
        val day = today ?: return null
        return WorkHoursRules.summary(shifts, date, day, nowLocal)
    }

    /** The day may be changed here: up to today, not closed, by whoever may confirm. */
    fun mayEdit(date: String): Boolean = mayConfirm && today != null && date <= today && date !in lockedDays

    /**
     * SH-128: the day's open shifts nobody said who worked yet. Today only those that started
     * (SH-262: an afternoon shift isn't asked about in the morning).
     */
    fun askingWhoWorked(date: String): List<Shift> {
        val upTo = today ?: return emptyList()
        return shifts.filter { it.date == date && it.asksWhoWorked(upTo) && (date < upTo || it.plannedTime.start.take(16) <= nowLocal.take(16)) }
            .sortedBy { it.plannedTime.start }
    }

    /** SH-262: the day's open shifts marked "Nobody worked", which may still be changed. */
    fun nobodyWorked(date: String): List<Shift> =
        shifts.filter { it.date == date && it.userId == null && it.actualUserId == null && it.notWorked }.sortedBy { it.plannedTime.start }

    /** The days of the month on screen, in order. */
    val days: List<String>
        get() {
            val m = month ?: return emptyList()
            var day = LocalDate.parse("$m-01")
            return buildList {
                while (day.toString().startsWith(m)) {
                    add(day.toString())
                    day = day.plus(DatePeriod(days = 1))
                }
            }
        }
}

/**
 * SH-257: "Godziny pracy": the day's shifts with the planned, clocked (or entered) and confirmed
 * hours, and one tap to confirm them as planned or as clocked, to type them, or (SH-236) to say who
 * actually worked; "Potwierdź dzień" confirms the rest. Day by day, the month's shifts followed
 * live; a confirmation shows at once and goes back if the server refuses it. Owners and managers
 * (Capability.EDIT_ROSTER); nothing is read otherwise.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class WorkHoursViewModel(
    private val workplaceId: String,
    initialDate: String?,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val confirmWorkHours: ConfirmWorkHoursUseCase,
    private val financialRepository: FinancialRepository? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val recordActualWork: RecordActualWorkUseCase? = null,
    private val splitShift: SplitShiftUseCase? = null,
    private val undoShiftSplit: UndoShiftSplitUseCase? = null,
    private val recordShiftWorker: RecordShiftWorkerUseCase? = null,
    private val now: () -> Instant = { AppClock.now() }
) : ViewModel() {

    private val _uiState = MutableStateFlow(WorkHoursUiState(selectedDate = initialDate, actualWorkOffered = recordActualWork != null))
    val uiState: StateFlow<WorkHoursUiState> = _uiState.asStateFlow()

    private val monthFlow = MutableStateFlow<String?>(null)
    private var timeZone: TimeZone = TimeZone.currentSystemDefault()
    private var editorUserId: String? = null
    private var loading: Job? = null
    /** The month's shifts as the server last sent them. */
    private var stored: List<Shift> = emptyList()
    /** Shifts shown as a change leaves them, until the server's next snapshot (or a refusal). */
    private val overlays = mutableMapOf<String, Shift>()
    /** Overlays whose change went through: dropped with the next snapshot. */
    private val settled = mutableSetOf<String>()
    private val askedNames = HashSet<String>()

    init {
        load()
    }

    private fun load() {
        loading?.cancel()
        loading = viewModelScope.launch {
            try {
                editorUserId = getCurrentUserUseCase().first()?.uid
                val capabilities = capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId).first()
                _uiState.update { it.copy(capabilities = capabilities) }
                if (!capabilities.shows(Gate.WORK_HOURS)) {
                    _uiState.update { it.copy(isLoading = false) }
                    return@launch
                }
                val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
                timeZone = timeZoneOrNull(workplace.settings.timezone) ?: TimeZone.currentSystemDefault()
                val clock = clockNow()
                val selected = _uiState.value.selectedDate?.takeIf { runCatching { LocalDate.parse(it) }.isSuccess } ?: clock.first
                _uiState.update { it.copy(today = clock.first, nowLocal = clock.second, selectedDate = selected) }
                val team = userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
                _uiState.update { state ->
                    state.copy(
                        staff = team.filter { it.displayName.isNotBlank() }.sortedBy { it.displayName.lowercase() },
                        names = team.associate { it.uid to it.displayName }.filterValues { it.isNotBlank() } + state.names
                    )
                }
                monthFlow.value = selected.take(7)
                monthFlow.filterNotNull().distinctUntilChanged().flatMapLatest { m ->
                    val rosterId = rosterIdOf(m)
                    readLockedDays(rosterId, capabilities)
                    shiftRepository.observeShiftsForRoster(rosterId).map { m to it }.catch { e ->
                        _uiState.update { s -> if (s.shifts.isEmpty()) s.copy(isLoading = false, loadError = e.toUiText()) else s.copy(error = e.toUiText()) }
                    }
                }.collect { (m, shifts) ->
                    // A month left behind (moved on to the next) keeps no overlays.
                    if (_uiState.value.month != m) {
                        overlays.clear()
                        settled.clear()
                    }
                    stored = shifts
                    settled.forEach { overlays.remove(it) }
                    settled.clear()
                    nameEveryone(shifts)
                    val clockNow = clockNow()
                    _uiState.update {
                        it.copy(
                            isLoading = false, loadError = null, month = m, shifts = shown(),
                            today = clockNow.first, nowLocal = clockNow.second
                        )
                    }
                }
            } catch (e: kotlinx.coroutines.CancellationException) {
                throw e
            } catch (e: Exception) {
                _uiState.update { it.copy(isLoading = false, loadError = e.toUiText()) }
            }
        }
    }

    /** The workplace's today and its local time now. */
    private fun clockNow(): Pair<String, String> {
        val local = now().toLocalDateTime(timeZone)
        val text = local.toString().let { if (it.length == 16) "$it:00" else it.take(19) }
        return local.date.toString() to text
    }

    private fun rosterIdOf(month: String) = RosterIds.of(workplaceId, month.take(4).toInt(), month.substring(5, 7).toInt())

    private suspend fun readLockedDays(rosterId: String, capabilities: Set<Capability>) {
        // The daily results are the money's (owners and financial managers); for others the server
        // refuses a closed day (reviewClock's DAY_CLOSED).
        val locked = if (capabilities.shows(Gate.MONEY)) {
            financialRepository?.getDailyRecordsForRoster(rosterId)?.getOrNull().orEmpty().filter { it.isLocked }.map { it.date }.toSet()
        } else emptySet()
        _uiState.update { it.copy(lockedDays = locked) }
    }

    /** SH-235: people on the month's shifts who are not in the team (left, from elsewhere) by their profile. */
    private suspend fun nameEveryone(shifts: List<Shift>) {
        val resolver = ResolvePersonNamesUseCase(userRepository)
        val ids = resolver.peopleOn(shifts) + shifts.mapNotNull { it.unplanned?.userId }
        val unknown = ids.filter { it !in _uiState.value.names && askedNames.add(it) }
        if (unknown.isEmpty()) return
        val found = resolver(workplaceId, unknown, team = _uiState.value.staff)
        _uiState.update { it.copy(names = it.names + found) }
    }

    private fun shown(): List<Shift> {
        val ids = stored.map { it.id }.toSet()
        return stored.map { overlays[it.id] ?: it } + overlays.values.filter { it.id !in ids }
    }

    private fun refresh() = _uiState.update { it.copy(shifts = shown()) }

    // --- The day ----------------------------------------------------------------------------

    fun selectDay(date: String) {
        if (runCatching { LocalDate.parse(date) }.isFailure) return
        _uiState.update { it.copy(selectedDate = date) }
        monthFlow.value = date.take(7)
    }

    fun previousDay() = moveDay(-1)

    fun nextDay() = moveDay(1)

    private fun moveDay(by: Int) {
        val current = _uiState.value.selectedDate ?: return
        selectDay(LocalDate.parse(current).plus(DatePeriod(days = by)).toString())
    }

    fun retry() {
        _uiState.update { it.copy(loadError = null, isLoading = true) }
        load()
    }

    fun messageShown() = _uiState.update { it.copy(message = null, error = null) }

    // --- Confirming -------------------------------------------------------------------------

    private fun rowOf(shiftId: String): WorkHoursRow? {
        val state = _uiState.value
        val shift = state.shifts.firstOrNull { it.id == shiftId } ?: return null
        return state.rows(shift.date).firstOrNull { it.shiftId == shiftId }
    }

    /** "Zgodnie z planem". */
    fun asPlanned(shiftId: String) {
        val row = rowOf(shiftId) ?: return
        WorkHoursRules.asPlanned(row)?.let { confirm(row.shift.date, listOf(row to it)) }
    }

    /** "Jak odbito" (or "Potwierdź" for hours the person entered). */
    fun asClocked(shiftId: String) {
        val row = rowOf(shiftId) ?: return
        WorkHoursRules.asClocked(row)?.let { confirm(row.shift.date, listOf(row to it)) }
    }

    /** The times typed in the edit dialog ([times] local ISO; the end the next day for a night shift). */
    fun editTimes(shiftId: String, times: TimeRange) {
        val row = rowOf(shiftId) ?: return
        if (times.start.isEmpty() || times.end.isEmpty() || times.end <= times.start) return
        confirm(row.shift.date, listOf(row to WorkHoursDecision(shiftId, com.evoapps.evoshift.domain.model.ClockReviewAction.EDIT_TIMES, times.start, times.end)))
    }

    /** "Potwierdź dzień": everything still waiting on the selected day. */
    fun confirmDay() {
        val state = _uiState.value
        val date = state.selectedDate ?: return
        val rows = state.rows(date).filter { it.shiftId !in state.busy }
        val decisions = WorkHoursRules.confirmDay(rows)
        if (decisions.isEmpty()) return
        confirm(date, decisions.mapNotNull { d -> rows.firstOrNull { it.shiftId == d.shiftId }?.let { it to d } })
    }

    /**
     * Optimistic: the rows show confirmed at once; each refused one goes back and says why. The
     * cost and the day's figures follow on the server and for those who see the money.
     */
    private fun confirm(date: String, decisions: List<Pair<WorkHoursRow, WorkHoursDecision>>) {
        val state = _uiState.value
        if (!state.mayEdit(date) || decisions.isEmpty()) return
        val pending = decisions.filter { (row, _) -> row.shiftId !in state.busy }
        if (pending.isEmpty()) return
        val at = now().toString()
        val by = editorUserId.orEmpty()
        val before = pending.associate { (row, _) -> row.shiftId to overlays[row.shiftId] }
        pending.forEach { (row, decision) ->
            WorkHoursRules.confirmedShift(row, decision, by, at)?.let { overlays[row.shiftId] = it }
        }
        val ids = pending.map { it.first.shiftId }.toSet()
        _uiState.update { it.copy(shifts = shown(), busy = it.busy + ids, message = null, error = null) }
        viewModelScope.launch {
            confirmWorkHours(workplaceId, rosterIdOf(date.take(7)), date, pending.map { it.second }).fold(
                onSuccess = { outcome ->
                    outcome.failed.keys.forEach { id -> before[id]?.let { overlays[id] = it } ?: overlays.remove(id) }
                    settled += outcome.confirmed
                    _uiState.update {
                        it.copy(
                            shifts = shown(),
                            busy = it.busy - ids,
                            message = when {
                                outcome.failed.isNotEmpty() -> UiText.Resource(
                                    Res.string.work_hours_done_partly, outcome.confirmed.size, outcome.failed.size,
                                    outcome.failed.values.first().toUiText()
                                )
                                outcome.missingPayRates -> UiText.Resource(Res.string.error_no_pay_rates)
                                pending.size == 1 -> UiText.Resource(Res.string.work_hours_done_one)
                                else -> UiText.Resource(Res.string.work_hours_done_day, outcome.confirmed.size)
                            }
                        )
                    }
                },
                onFailure = { e ->
                    ids.forEach { id -> before[id]?.let { overlays[id] = it } ?: overlays.remove(id) }
                    _uiState.update { it.copy(shifts = shown(), busy = it.busy - ids, error = e.toUiText()) }
                }
            )
        }
    }

    // --- SH-236: who actually worked, splitting -----------------------------------------------

    /** "Nie przyszedł · Zastępstwo · Podział": who worked [shiftId] and when (the SH-236 dialog). */
    fun recordActualWork(shiftId: String, worker: ActualWorker, actual: TimeRange?, absenceType: AbsenceType?) {
        val useCase = recordActualWork ?: return
        val editor = editorUserId ?: return
        val shift = _uiState.value.shifts.firstOrNull { it.id == shiftId } ?: return
        if (!_uiState.value.mayEdit(shift.date)) return
        if (worker == ActualWorker.AsPlanned && shift.userId == null) return
        val nobody = worker is ActualWorker.Nobody
        val shownShift = shift.copy(
            actualUserId = (worker as? ActualWorker.Person)?.userId?.takeIf { it != shift.userId },
            notWorked = nobody,
            noShowReason = (worker as? ActualWorker.Nobody)?.reason,
            actualTime = when {
                nobody -> null
                actual != null -> actual
                else -> shift.actualTime ?: shift.plannedTime
            }
        )
        runChange(shiftId, shownShift) {
            useCase(workplaceId, shift.rosterId, shiftId, worker, actual, editor, absenceType).map { outcome ->
                when {
                    nobody -> UiText.Resource(Res.string.actual_work_saved_nobody)
                    outcome.missingPayRates -> UiText.Resource(Res.string.error_no_pay_rates)
                    outcome.shift.actualUserId == null -> UiText.Resource(Res.string.actual_work_saved_planned)
                    else -> UiText.Resource(Res.string.who_worked_recorded, personNameArg(_uiState.value.nameOf(outcome.shift.actualUserId!!)))
                }
            }
        }
    }

    fun splitShift(shiftId: String, request: SplitRequest) {
        val useCase = splitShift ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        val shift = state.shifts.firstOrNull { it.id == shiftId } ?: return
        if (!state.mayEdit(shift.date)) return
        val first = runCatching {
            ShiftSplits.split(
                shift, request.handoverAt, request.replacementId, state.nameOf(request.replacementId), request.reason, request.sickness,
                editor, now().toString(), planned = false
            ).first
        }.getOrElse { e ->
            _uiState.update { it.copy(error = e.toUiText()) }
            return
        }
        runChange(shiftId, first) {
            useCase(
                workplaceId, shift.rosterId, shiftId, request.handoverAt, request.replacementId, request.reason, request.sickness,
                request.recordSickLeave, editor, state.today ?: shift.date
            ).map { null }
        }
    }

    fun undoSplit(shiftId: String) {
        val useCase = undoShiftSplit ?: return
        val editor = editorUserId ?: return
        val shift = _uiState.value.shifts.firstOrNull { it.id == shiftId } ?: return
        if (!_uiState.value.mayEdit(shift.date) || shift.split == null) return
        runChange(shiftId, null) {
            useCase(workplaceId, shift.rosterId, shiftId, editor).map { UiText.Resource(Res.string.split_undone) }
        }
    }

    /** SH-128: who worked an open shift nobody was assigned to (null: nobody). */
    fun recordWorker(shiftId: String, workerId: String?) {
        val useCase = recordShiftWorker ?: return
        val editor = editorUserId ?: return
        val shift = _uiState.value.shifts.firstOrNull { it.id == shiftId } ?: return
        if (!_uiState.value.mayEdit(shift.date)) return
        val shownShift = shift.copy(
            actualUserId = workerId,
            notWorked = workerId == null,
            actualTime = if (workerId != null && shift.actualTime == null) shift.plannedTime else shift.actualTime
        )
        runChange(shiftId, shownShift) {
            useCase(workplaceId, shift.rosterId, shiftId, workerId, editor).map { outcome ->
                when {
                    workerId == null -> UiText.Resource(Res.string.who_worked_nobody_done)
                    outcome.missingPayRates -> UiText.Resource(Res.string.who_worked_missing_rates, personNameArg(_uiState.value.nameOf(workerId)))
                    else -> UiText.Resource(Res.string.who_worked_recorded, personNameArg(_uiState.value.nameOf(workerId)))
                }
            }
        }
    }

    /** [shown] at once (null: as stored), the [save] in the background; a refusal puts it back. */
    private fun runChange(shiftId: String, shown: Shift?, save: suspend () -> Result<UiText?>) {
        if (shiftId in _uiState.value.busy) return
        val before = overlays[shiftId]
        if (shown != null) overlays[shiftId] = shown
        _uiState.update { it.copy(shifts = shown(), busy = it.busy + shiftId, message = null, error = null) }
        viewModelScope.launch {
            save().fold(
                onSuccess = { text ->
                    settled += shiftId
                    _uiState.update { it.copy(busy = it.busy - shiftId, message = text) }
                },
                onFailure = { e ->
                    if (before != null) overlays[shiftId] = before else overlays.remove(shiftId)
                    _uiState.update { it.copy(shifts = shown(), busy = it.busy - shiftId, error = e.toUiText()) }
                }
            )
        }
    }
}
